import type { ReactNode } from "react";

export type HistoryAmountTone = "income" | "expense" | "receivable";

const TONE_CLASS: Record<HistoryAmountTone, string> = {
  income: "font-semibold tabular-nums text-aera-green",
  expense: "font-semibold tabular-nums text-aera-red",
  receivable: "font-semibold tabular-nums text-aera-amber",
};

/** Name renames — digits here are part of the name (e.g. เรนจัง 2), not money. */
const NAME_CHANGE_TEXT_REGEX =
  /แก้ชื่อลูกค้า|แก้(?:ชื่อ)?ผู้โอน|^ชื่อลูกค้า\s*:/;

/**
 * Money amounts only:
 * - ฿… / …บาท
 * - left side of "1500 → 2000 บาท"
 * Does not match bare digits in names like "เรนจัง 2".
 */
const AMOUNT_REGEX =
  /(?<![\p{L}\p{N}])(?:฿\s*[\d,]+(?:\.\d+)?(?![\p{L}\p{N}])|[\d,]+(?:\.\d+)?\s*บาท|[\d,]+(?:\.\d+)?(?=\s*[→➞]\s*[\d,]+(?:\.\d+)?\s*บาท))/gu;

export function historyAmountToneClass(tone: HistoryAmountTone): string {
  return TONE_CLASS[tone];
}

export function renderHistoryAmountText(
  text: string,
  tone: HistoryAmountTone,
): ReactNode {
  if (!text?.trim()) return text;
  if (NAME_CHANGE_TEXT_REGEX.test(text)) return text;

  const className = TONE_CLASS[tone];
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(AMOUNT_REGEX)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }
    parts.push(
      <span key={`amt-${key++}`} className={className}>
        {match[0]}
      </span>,
    );
    lastIndex = index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

export function HistoryAmountText({
  text,
  tone,
  className = "",
}: {
  text: string;
  tone: HistoryAmountTone;
  className?: string;
}) {
  return <span className={className}>{renderHistoryAmountText(text, tone)}</span>;
}

export function resolveHistoryEntryAmountTone(
  entry: { type?: string; title?: string },
  fallback: HistoryAmountTone = "receivable",
): HistoryAmountTone {
  if (entry.type === "unmatched" || /ยกเลิกจับคู่/.test(entry.title ?? "")) {
    return "expense";
  }
  if (entry.type === "matched" || /จับคู่รับเงิน/.test(entry.title ?? "")) {
    return "income";
  }
  return fallback;
}

/** @deprecated Use resolveHistoryEntryAmountTone */
export function resolveReceivableHistoryAmountTone(entry: {
  type?: string;
  title?: string;
}): HistoryAmountTone {
  return resolveHistoryEntryAmountTone(entry);
}
